import React, { useState, useMemo } from 'react';
import { Table, InputNumber, Checkbox, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';

const QuincenaTable = ({ quincenaData, onUpdatePago, disabled }) => {
    const { quincena, trabajadores, obras } = quincenaData;
    const [editingCell, setEditingCell] = useState(null);

    // Función para formatear moneda
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value || 0);
    };

    // Calcular valores derivados para un trabajador
    const calcularValores = (trabajador) => {
        const pagoQuincenal = parseFloat(trabajador.pago_quincenal) || 0;
        const incidencias = parseFloat(trabajador.incidencias) || 0;
        const totalFiscal = parseFloat(trabajador.total_fiscal) || 0;
        const totalNoFiscal = parseFloat(trabajador.total_no_fiscal) || 0;

        const obrasAsignadas = trabajador.obras_ids ? trabajador.obras_ids.split(',').length : 0;
        const division = obrasAsignadas || 1;
        const desgloseFiscal = totalFiscal / division;
        const desgloseNoFiscal = totalNoFiscal / division;
        const totalPercibido = pagoQuincenal - incidencias;

        return {
            totalFiscal,
            totalNoFiscal,
            division,
            desgloseFiscal,
            desgloseNoFiscal,
            totalPercibido
        };
    };

    // Manejar cambio en una celda editable
    const handleCellChange = (trabajadorId, field, value) => {
        const trabajador = trabajadores.find(t => t.trabajador_id === trabajadorId);
        if (!trabajador) return;

        const pagoQuincenal = parseFloat(trabajador.pago_quincenal) || 0;
        const incidencias = parseFloat(trabajador.incidencias) || 0;
        const totalPercibido = pagoQuincenal - incidencias;

        let datos = {
            pago_quincenal: pagoQuincenal,
            incidencias: incidencias,
            total_fiscal: parseFloat(trabajador.total_fiscal) || 0,
            total_no_fiscal: parseFloat(trabajador.total_no_fiscal) || 0,
            [field]: value
        };

        // Auto-calcular complemento cuando se edita total_fiscal
        if (field === 'total_fiscal') {
            datos.total_no_fiscal = Math.max(0, totalPercibido - value);
        }
        // Auto-calcular complemento cuando se edita total_no_fiscal
        else if (field === 'total_no_fiscal') {
            datos.total_fiscal = Math.max(0, totalPercibido - value);
        }
        // Recalcular ambos cuando se edita pago_quincenal o incidencias
        else if (field === 'pago_quincenal' || field === 'incidencias') {
            const nuevoTotalPercibido = field === 'pago_quincenal' ? value - incidencias : pagoQuincenal - value;
            // Mantener la proporción de fiscal/no fiscal si ya existen
            const totalActual = datos.total_fiscal + datos.total_no_fiscal;
            if (totalActual > 0) {
                const proporcionFiscal = datos.total_fiscal / totalActual;
                datos.total_fiscal = nuevoTotalPercibido * proporcionFiscal;
                datos.total_no_fiscal = nuevoTotalPercibido * (1 - proporcionFiscal);
            }
        }

        onUpdatePago(trabajadorId, datos);
        setEditingCell(null);
    };

    // Componente de celda editable
    const EditableCell = ({ value, field, trabajadorId, bgColor }) => {
        if (disabled) return formatCurrency(value);

        const isEditing = editingCell === `${trabajadorId}_${field}`;
        if (isEditing) {
            return (
                <InputNumber
                    autoFocus
                    defaultValue={value}
                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/\$\s?|(,*)/g, '')}
                    onBlur={(e) => handleCellChange(trabajadorId, field, parseFloat(e.target.value.replace(/[$,]/g, '')) || 0)}
                    onPressEnter={(e) => handleCellChange(trabajadorId, field, parseFloat(e.target.value.replace(/[$,]/g, '')) || 0)}
                    style={{ width: '100%' }}
                />
            );
        }
        return (
            <div
                onClick={() => setEditingCell(`${trabajadorId}_${field}`)}
                style={{ cursor: 'pointer', padding: '4px 8px', background: bgColor, borderRadius: 4 }}
            >
                {formatCurrency(value)}
            </div>
        );
    };

    // Generar columnas dinámicas para las obras
    const obrasColumns = obras.map(obra => ({
        title: (
            <Tooltip title={obra.nombre_obra}>
                <span style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontSize: 11,
                    maxHeight: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {obra.nombre_obra}
                </span>
            </Tooltip>
        ),
        dataIndex: `obra_${obra.id}`,
        key: `obra_${obra.id}`,
        width: 40,
        align: 'center',
        render: (_, record) => {
            const obrasIds = record.obras_ids ? record.obras_ids.split(',') : [];
            const estaAsignado = obrasIds.includes(String(obra.id));
            return (
                <Checkbox
                    checked={estaAsignado}
                    disabled={true}
                    style={{ opacity: estaAsignado ? 1 : 0.3 }}
                />
            );
        }
    }));

    // Columnas de la tabla
    const columns = [
        {
            title: 'NOMBRE',
            dataIndex: 'nombre',
            key: 'nombre',
            fixed: 'left',
            width: 150,
            render: (text) => <strong>{text}</strong>
        },
        ...obrasColumns,
        {
            title: 'PAGO QUINCENAL',
            dataIndex: 'pago_quincenal',
            key: 'pago_quincenal',
            width: 130,
            align: 'right',
            render: (value, record) => (
                <EditableCell
                    value={value}
                    field="pago_quincenal"
                    trabajadorId={record.trabajador_id}
                    bgColor="#fffbe6"
                />
            )
        },
        {
            title: 'INCIDENCIAS',
            dataIndex: 'incidencias',
            key: 'incidencias',
            width: 120,
            align: 'right',
            render: (value, record) => (
                <EditableCell
                    value={value}
                    field="incidencias"
                    trabajadorId={record.trabajador_id}
                    bgColor="#fff1f0"
                />
            )
        },
        {
            title: 'TOTAL FISCAL',
            dataIndex: 'total_fiscal',
            key: 'total_fiscal',
            width: 120,
            align: 'right',
            render: (value, record) => (
                <EditableCell
                    value={value}
                    field="total_fiscal"
                    trabajadorId={record.trabajador_id}
                    bgColor="#f6ffed"
                />
            )
        },
        {
            title: 'TOTAL NO FISCAL',
            dataIndex: 'total_no_fiscal',
            key: 'total_no_fiscal',
            width: 130,
            align: 'right',
            render: (value, record) => (
                <EditableCell
                    value={value}
                    field="total_no_fiscal"
                    trabajadorId={record.trabajador_id}
                    bgColor="#fff7e6"
                />
            )
        },
        {
            title: 'DIVISIÓN',
            key: 'division',
            width: 80,
            align: 'center',
            render: (_, record) => {
                const { division } = calcularValores(record);
                return <span style={{ fontWeight: 600 }}>{division}</span>;
            }
        },
        {
            title: 'DESGLOSE FISCAL',
            key: 'desglose_fiscal',
            width: 130,
            align: 'right',
            render: (_, record) => {
                const { desgloseFiscal } = calcularValores(record);
                return (
                    <span style={{ color: '#52c41a', fontWeight: 500 }}>
                        {formatCurrency(desgloseFiscal)}
                    </span>
                );
            }
        },
        {
            title: 'DESGLOSE NO FISCAL',
            key: 'desglose_no_fiscal',
            width: 140,
            align: 'right',
            render: (_, record) => {
                const { desgloseNoFiscal } = calcularValores(record);
                return (
                    <span style={{ color: '#faad14', fontWeight: 500 }}>
                        {formatCurrency(desgloseNoFiscal)}
                    </span>
                );
            }
        },
        {
            title: 'TOTAL PERCIBIDO',
            key: 'total_percibido',
            width: 130,
            align: 'right',
            fixed: 'right',
            render: (_, record) => {
                const { totalPercibido } = calcularValores(record);
                return (
                    <span style={{
                        color: '#1890ff',
                        fontWeight: 700,
                        fontSize: 14
                    }}>
                        {formatCurrency(totalPercibido)}
                    </span>
                );
            }
        }
    ];

    // Calcular totales
    const totales = useMemo(() => {
        return trabajadores.reduce((acc, t) => {
            const valores = calcularValores(t);
            return {
                pagoQuincenal: acc.pagoQuincenal + (parseFloat(t.pago_quincenal) || 0),
                incidencias: acc.incidencias + (parseFloat(t.incidencias) || 0),
                totalFiscal: acc.totalFiscal + (parseFloat(t.total_fiscal) || 0),
                totalNoFiscal: acc.totalNoFiscal + (parseFloat(t.total_no_fiscal) || 0),
                desgloseFiscal: acc.desgloseFiscal + valores.desgloseFiscal,
                desgloseNoFiscal: acc.desgloseNoFiscal + valores.desgloseNoFiscal,
                totalPercibido: acc.totalPercibido + valores.totalPercibido
            };
        }, {
            pagoQuincenal: 0,
            incidencias: 0,
            totalFiscal: 0,
            totalNoFiscal: 0,
            desgloseFiscal: 0,
            desgloseNoFiscal: 0,
            totalPercibido: 0
        });
    }, [trabajadores]);

    return (
        <div>
            {disabled && (
                <div style={{
                    background: '#fff1f0',
                    padding: '8px 16px',
                    borderRadius: 4,
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <LockOutlined style={{ color: '#ff4d4f' }} />
                    <span>Esta quincena está cerrada. No se pueden hacer modificaciones.</span>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={trabajadores.map(t => ({ ...t, key: t.trabajador_id }))}
                pagination={false}
                scroll={{ x: 1500 }}
                size="small"
                bordered
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                            <Table.Summary.Cell index={0}>TOTALES</Table.Summary.Cell>
                            {obras.map((_, idx) => (
                                <Table.Summary.Cell key={idx} index={idx + 1}>-</Table.Summary.Cell>
                            ))}
                            <Table.Summary.Cell align="right">{formatCurrency(totales.pagoQuincenal)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="right">{formatCurrency(totales.incidencias)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#52c41a' }}>{formatCurrency(totales.totalFiscal)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#faad14' }}>{formatCurrency(totales.totalNoFiscal)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="center">-</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#52c41a' }}>{formatCurrency(totales.desgloseFiscal)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#faad14' }}>{formatCurrency(totales.desgloseNoFiscal)}</Table.Summary.Cell>
                            <Table.Summary.Cell align="right" style={{ color: '#1890ff' }}>{formatCurrency(totales.totalPercibido)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </div>
    );
};

export default QuincenaTable;
